import { useCallback, useState } from 'react';

type Cursor = string;

type Edge = {
  cursor: Cursor;
};

type PageInfo = {
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
};

type Data = {
  edges: (Edge | null)[];
  pageInfo: PageInfo;
};

type PaginationVariables = {
  limit?: number;
  cursor?: Cursor;
};

type TablePaginationInternal = {
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNext: () => void;
  onPrevious: () => void;
};

export type ForwardCursorPagination = {
  paginationVariables: PaginationVariables;
  resetPagination: () => void;
  tablePagination: TablePaginationInternal;
  updatePagination: (data: Data) => void;
};

/**
 * @deprecated Use useCursorBasedPagination from @anchorage/hooks instead. Please check
 * that the query you're calling works correctly with the pageInfo startCursor
 * and endCursor, otherwise V2 will not work properly.
 */
export function useForwardCursorPagination(
  pageSize: number,
): ForwardCursorPagination {
  const limit: number = pageSize;

  const [paginationVariables, setPaginationVariables] =
    useState<PaginationVariables>({ limit });
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [cursors, setCursors] = useState<Cursor[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);

  const hasPreviousPage: boolean = currentIndex > 0;

  function tablePaginationOnLoading() {
    setHasNextPage(false);
  }

  function onNext() {
    tablePaginationOnLoading();
    setCurrentIndex((currentIndex) => currentIndex + limit);
    setPaginationVariables({
      limit,
      cursor: cursors[cursors.length - 1],
    });
  }

  function onPrevious() {
    tablePaginationOnLoading();

    if (currentIndex <= limit) {
      setHasNextPage(true);
      setCursors([]);
      setCurrentIndex(0);
      setPaginationVariables({ limit });
    } else {
      const newCurrentIndex = currentIndex - limit;
      setCursors((cursors) => cursors.slice(0, newCurrentIndex));
      setCurrentIndex((currentIndex) => currentIndex - limit);
      setPaginationVariables({
        limit,
        cursor: cursors[newCurrentIndex - 1],
      });
    }
  }

  // Stable identity across renders: consumers call this from a useEffect keyed
  // on the query `data`, so an unstable identity would re-fire that effect every
  // render and loop. Only the `data` arg and functional setState are used here.
  const updatePagination = useCallback((data: Data) => {
    const { edges, pageInfo } = data;
    const validEdges = edges.filter((edge) => edge != null) as Edge[];
    const newCursors: Cursor[] = validEdges.map((edge) => edge.cursor);
    setHasNextPage(!!pageInfo.hasNextPage);
    setCursors((cursors) => [...cursors, ...newCursors]);
  }, []);

  const resetPagination = useCallback(() => {
    setHasNextPage(false);
    setCursors([]);
    setCurrentIndex(0);
    setPaginationVariables({ limit });
  }, [limit]);

  return {
    paginationVariables,
    resetPagination,
    tablePagination: {
      pageSize: limit,
      hasNextPage,
      hasPreviousPage,
      onNext,
      onPrevious,
    },
    updatePagination,
  };
}
